import { useState, useEffect, useRef } from "react";

type TimerPhase = "delay" | "active" | "completed";

interface UseSunbedTimerProps {
  /** Session countdown seconds from API (active phase). */
  initialRemaining: number;
  /** Delay countdown seconds from API. */
  initialDelay?: number;
  /** Absolute session start (ISO). Preferred over second snapshots. */
  startAt?: string | null;
  /** Absolute session end = start + duration (ISO). */
  endsAt?: string | null;
  /** Server clock at response time (ISO) — corrects client clock skew. */
  serverNow?: string | null;
  onComplete?: () => void;
  onPhaseChange?: (phase: TimerPhase) => void;
  isActive?: boolean;
}

function parseMs(value?: string | null): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function formatTime(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Live sunbed countdown driven by absolute clocks when available.
 * Re-syncs whenever API values change so kiosk + web stay aligned.
 */
export const useSunbedTimer = ({
  initialRemaining,
  initialDelay = 0,
  startAt,
  endsAt,
  serverNow,
  onComplete,
  onPhaseChange,
  isActive = true,
}: UseSunbedTimerProps) => {
  const onCompleteRef = useRef(onComplete);
  const onPhaseChangeRef = useRef(onPhaseChange);
  const completedFiredRef = useRef(false);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onPhaseChangeRef.current = onPhaseChange;
  }, [onComplete, onPhaseChange]);

  const startMs = parseMs(startAt);
  const endsMs = parseMs(endsAt);
  const serverMs = parseMs(serverNow);
  // Offset so client Date.now() tracks server time
  const skewRef = useRef(0);
  if (serverMs != null) {
    skewRef.current = serverMs - Date.now();
  }

  const compute = () => {
    const now = Date.now() + skewRef.current;

    if (startMs != null && endsMs != null) {
      if (now < startMs) {
        return {
          phase: "delay" as TimerPhase,
          delayLeft: Math.max(0, Math.ceil((startMs - now) / 1000)),
          secondsLeft: Math.max(0, Math.ceil((endsMs - startMs) / 1000)),
        };
      }
      const left = Math.max(0, Math.ceil((endsMs - now) / 1000));
      return {
        phase: (left > 0 ? "active" : "completed") as TimerPhase,
        delayLeft: 0,
        secondsLeft: left,
      };
    }

    // Fallback: relative snapshots from API
    if (initialDelay > 0) {
      return {
        phase: "delay" as TimerPhase,
        delayLeft: initialDelay,
        secondsLeft: initialRemaining,
      };
    }
    if (initialRemaining > 0) {
      return {
        phase: "active" as TimerPhase,
        delayLeft: 0,
        secondsLeft: initialRemaining,
      };
    }
    return {
      phase: "completed" as TimerPhase,
      delayLeft: 0,
      secondsLeft: 0,
    };
  };

  const [phase, setPhase] = useState<TimerPhase>(() => compute().phase);
  const [delayLeft, setDelayLeft] = useState(() => compute().delayLeft);
  const [secondsLeft, setSecondsLeft] = useState(() => compute().secondsLeft);

  // Resync whenever server snapshot / clocks change
  useEffect(() => {
    if (!isActive) {
      setPhase("completed");
      setDelayLeft(0);
      setSecondsLeft(0);
      return;
    }

    completedFiredRef.current = false;
    const next = compute();
    setPhase(next.phase);
    setDelayLeft(next.delayLeft);
    setSecondsLeft(next.secondsLeft);
    onPhaseChangeRef.current?.(next.phase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, initialRemaining, initialDelay, startAt, endsAt, serverNow]);

  useEffect(() => {
    if (!isActive) return;
    if (phase === "completed") {
      if (!completedFiredRef.current) {
        completedFiredRef.current = true;
        onCompleteRef.current?.();
      }
      return;
    }

    const tick = () => {
      const next = compute();
      setPhase((prev) => {
        if (prev !== next.phase) onPhaseChangeRef.current?.(next.phase);
        return next.phase;
      });
      setDelayLeft(next.delayLeft);
      setSecondsLeft(next.secondsLeft);

      if (next.phase === "completed" && !completedFiredRef.current) {
        completedFiredRef.current = true;
        onCompleteRef.current?.();
      }
    };

    const intervalId = setInterval(tick, 250);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, phase, startAt, endsAt, serverNow, initialRemaining, initialDelay]);

  return {
    phase,
    delayLeft,
    secondsLeft,
    displayTime:
      phase === "delay" ? formatTime(delayLeft) : formatTime(secondsLeft),
    isCompleted: phase === "completed" || !isActive,
  };
};
